'use client';

import { useTheme } from 'next-themes';
import { getI18n } from 'react-i18next';
import { Toaster as Sonner, toast as sonnerToast } from 'sonner';

import { parseValidationMessage } from '@kit/shared/validation';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton:
            'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton:
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
        },
      }}
      {...props}
    />
  );
};

/**
 * Validation messages are tokens (see @kit/shared/validation). Toasts are
 * called outside React, so the token is translated here with the instance
 * initReactI18next registered; any other text is shown as it is.
 */
function translateToastMessage(message: React.ReactNode): React.ReactNode {
  if (typeof message !== 'string') return message;
  const parsed = parseValidationMessage(message);
  const i18n = getI18n();
  if (!parsed || !i18n) return message;
  return i18n.t(parsed.key, { ...parsed.values, defaultValue: message });
}

type ToastArgs = Parameters<typeof sonnerToast>;

function translateOptions(options: ToastArgs[1]): ToastArgs[1] {
  if (!options?.description) return options;
  return {
    ...options,
    description: translateToastMessage(options.description as React.ReactNode),
  };
}

function wrap<
  F extends (message: ToastArgs[0], options?: ToastArgs[1]) => unknown,
>(fn: F): F {
  return ((message: ToastArgs[0], options?: ToastArgs[1]) =>
    fn(
      translateToastMessage(message as React.ReactNode) as ToastArgs[0],
      translateOptions(options),
    )) as F;
}

const toast = Object.assign(wrap(sonnerToast), {
  ...sonnerToast,
  success: wrap(sonnerToast.success),
  error: wrap(sonnerToast.error),
  warning: wrap(sonnerToast.warning),
  info: wrap(sonnerToast.info),
  message: wrap(sonnerToast.message),
});

export { Toaster, toast, translateToastMessage };
